import { inject, Injectable } from "@angular/core";
import { LiveAssetDetails, LiveAssetStatus } from "@rvi/asset-modules";
import { concatMap, from, map, mergeMap, Observable, tap, toArray } from "rxjs";
import { SubjectSchema } from "../../../_offline/models/subject.schema";
import { SubjectDbService } from "../../database/subject-db.service";
import { AssetModuleClient } from "../asset-module-client.service";
import { Log } from "./log.decorator";
import { CreatedPropertyDictionnary } from "./properties-migration.service";
import { IdsMap } from "./types";
import { SubjectAttributesSchema } from "../../../_offline/models/subject-attributes.schema";

type Request = {
  subjectId: string;
  version: number;
  assetTypeId: string;
  propertiesToUpdate: { id: string; value: string }[];
  propertiesToCreate: string[];
  status: LiveAssetStatus;
};

@Injectable({
  providedIn: "root"
})
export class SubjectsMigrationService {
  private assetModuleClient = inject(AssetModuleClient);
  private subjectDbService = inject(SubjectDbService);

  @Log("MigrateSubjects")
  public migrateSubjects(subjectModelIds: IdsMap, propertiesIds: CreatedPropertyDictionnary): Observable<IdsMap> {
    return this.subjectDbService.getAllLegacyOnPremise().pipe(
      map((subjects) => this.toLiveAssetRequest(subjects, subjectModelIds, propertiesIds)),
      concatMap((request) => this.createLiveAssets(request)),
      concatMap((results) =>
        from(results).pipe(
          concatMap(({ live, request }) => this.addPropertiesAndUpdateStatus(live, request)),
          toArray()
        )
      ),
      map((ids) => new IdsMap(ids.map(({ subjectId, liveId, version }) => [`${subjectId}${version}`, liveId])))
    );
  }

  createLiveAssets(requests: Request[]): Observable<{ live: LiveAssetDetails; request: Request }[]> {
    return from(requests).pipe(
      concatMap((request) => this.assetModuleClient.createLiveAsset(request.assetTypeId).pipe(map((live) => ({ live, request })))),
      toArray()
    );
  }

  addPropertiesAndUpdateStatus(liveAssetDetails: LiveAssetDetails, request: Request) {
    return this.addProperties(liveAssetDetails, request).pipe(
      concatMap(() => this.assetModuleClient.updateLiveAssetStatus(liveAssetDetails.id, request.status, true)),
      map((live) => ({ liveId: live.id, subjectId: request.subjectId, version: request.version }))
    );
  }

  private toLiveAssetRequest(subjects: SubjectSchema[], subjectModelIdsMap: IdsMap, propertiesIds: CreatedPropertyDictionnary): Request[] {
    return subjects
      .map((subject) => subject.subjectAttributes.map((att) => ({ attributes: att, subject, multipleVersion: subject.subjectAttributes.length > 1 })))
      .flat()
      .map(({ subject, attributes, multipleVersion }) => {
        const propertiesToUpdate = this.getPropertiesToUpdate(propertiesIds, attributes, multipleVersion, subject);

        return {
          subjectId: subject.id,
          version: attributes.version ?? 1,
          assetTypeId: subjectModelIdsMap.get(subject.subjectModelId),
          propertiesToCreate: propertiesToUpdate.filter((p) => p.id !== propertiesIds.SerialNumber.propertyId).map((p) => p.id),
          propertiesToUpdate: propertiesToUpdate,
          status: this.liveAssetStatus(subject.isValid, attributes.isNewestVersion)
        };
      });
  }

  private liveAssetStatus(isEnabled: boolean, isNewestVersion: boolean) {
    if (!isNewestVersion) {
      return LiveAssetStatus.Decommissioned;
    }

    if (isEnabled) {
      return LiveAssetStatus.Operational;
    }

    return LiveAssetStatus.OutOfService;
  }

  private getPropertiesToUpdate(
    propertiesIds: CreatedPropertyDictionnary,
    attributes: SubjectAttributesSchema,
    multipleVersion: boolean,
    subject: SubjectSchema
  ) {
    return [
      {
        id: propertiesIds.Country.propertyId,
        value: attributes.country
      },
      {
        id: propertiesIds.Owner.propertyId,
        value: attributes.owner
      },
      {
        id: propertiesIds.Project.propertyId,
        value: attributes.project
      },
      {
        id: propertiesIds.SerialNumber.propertyId,
        value: multipleVersion ? `${subject.serialNo} v${attributes.version}` : `${subject.serialNo}`
      },
      {
        id: propertiesIds.Site.propertyId,
        value: attributes.site
      },
      {
        id: propertiesIds.State.propertyId,
        value: attributes.state
      }
    ].filter((property) => !!property.value);
  }

  private addProperties(liveAsset: LiveAssetDetails, request: Request) {
    return this.assetModuleClient.createLiveAssetProperties(liveAsset.id, request.propertiesToCreate).pipe(
      mergeMap(() => this.assetModuleClient.getLiveAssetProperties(liveAsset.id)),
      concatMap((allLiveProperties) => {
        return this.assetModuleClient.updateLiveAssetProperties(
          liveAsset.id,
          request.propertiesToUpdate.map((property) => ({
            id: allLiveProperties.find((p) => p.propertyId == property.id)?.id,
            value: property.value
          }))
        );
      })
    );
  }
}
